import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { AlertTriangle, CheckCircle2, Save, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEV_BG = { Bassa: '#94a3b8', Media: '#f59e0b', Alta: '#ea580c', Critica: '#dc2626' };

/**
 * Portale pubblico per il fornitore: nessun login richiesto.
 * Accesso tramite URL con token univoco: /supplier-portal/8d/:token
 */
export default function SupplierPortal8DPage() {
  const { token } = useParams();
  const [nc, setNc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/supplier-nc/${token}`, { credentials: 'omit' });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${res.status}`);
        }
        setNc(await res.json());
      } catch (e) {
        setError(e.message || 'Errore caricamento');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const patch = (u) => setNc(prev => ({ ...prev, ...u }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        supplier_response_date: nc.supplier_response_date || null,
        supplier_response_text: nc.supplier_response_text || null,
        supplier_root_cause: nc.supplier_root_cause || null,
        supplier_corrective_action: nc.supplier_corrective_action || null,
        d1_team: nc.d1_team || null,
        d2_problem_description: nc.d2_problem_description || null,
        d3_containment_actions: nc.d3_containment_actions || null,
        d4_root_cause: nc.d4_root_cause || null,
        d5_corrective_actions: nc.d5_corrective_actions || null,
        d6_verification: nc.d6_verification || null,
        d7_prevention: nc.d7_prevention || null,
        d8_closure_notes: nc.d8_closure_notes || null,
      };
      const res = await fetch(`${BACKEND_URL}/api/public/supplier-nc/${token}/8d`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Errore salvataggio'); }
      toast.success('Risposta inviata con successo. Grazie!');
      setSaved(true);
    } catch (e) {
      toast.error(e.message || 'Errore invio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #0f172a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', fontFamily: 'Arial, sans-serif', padding: 20 }}>
        <div style={{ background: 'white', padding: 30, borderRadius: 12, maxWidth: 500, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <AlertTriangle style={{ width: 48, height: 48, color: '#dc2626' }} />
          <h1 style={{ marginTop: 12, color: '#991b1b' }}>Link non valido</h1>
          <p style={{ color: '#7f1d1d' }}>{error}</p>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 12 }}>Contatta Evotech Precision per ricevere un nuovo link.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Arial, sans-serif', paddingBottom: 40 }} data-testid="supplier-portal-page">
      <Toaster position="top-right" />
      {/* Header */}
      <div style={{ background: '#0f172a', color: 'white', padding: '20px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>Evotech Precision srl — Portale Fornitore</p>
          <h1 style={{ margin: '4px 0 0', fontSize: 22 }}>Report 8D per Non Conformità <span style={{ fontFamily: 'monospace', color: '#93c5fd' }}>{nc.supplier_nc_code}</span></h1>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 24px' }}>
        {saved && (
          <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: 16, borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle2 style={{ width: 20, height: 20 }} />
            <div>
              <b>Risposta salvata.</b> Puoi continuare a modificare i campi e salvare di nuovo finché non ricevi conferma di chiusura da Evotech.
            </div>
          </div>
        )}

        {/* NC info readonly */}
        <div style={{ background: 'white', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, color: '#334155' }}>Dati della Non Conformità</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <Info label="Fornitore" value={nc.supplier_name} />
            <Info label="Data emissione" value={nc.issue_date} mono />
            <Info label="Scadenza risposta" value={nc.response_deadline} mono highlight={nc.response_deadline && nc.response_deadline < new Date().toISOString().slice(0,10)} />
            <Info label="Gravità" value={<span style={{ background: SEV_BG[nc.severity] || '#94a3b8', color: 'white', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 'bold' }}>{nc.severity}</span>} />
            <Info label="Categoria difetto" value={nc.defect_category_name} />
            <Info label="Q.tà coinvolta" value={nc.quantity_involved} />
            <Info label="DDT fornitore" value={nc.supplier_ddt_number} />
            <Info label="Codice grezzo / prodotto" value={nc.raw_part_code || nc.product_code} />
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
            <p style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', letterSpacing: 0.5 }}>Descrizione difetto</p>
            <p style={{ margin: '4px 0 12px', color: '#0f172a', whiteSpace: 'pre-wrap' }}>{nc.defect_description || '—'}</p>
            <p style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', letterSpacing: 0.5 }}>Azione richiesta</p>
            <p style={{ margin: '4px 0 0', color: '#0f172a', whiteSpace: 'pre-wrap' }}>{nc.requested_action || '—'}</p>
          </div>
        </div>

        {/* Sezione risposta breve */}
        <div style={{ background: 'white', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, color: '#334155' }}>Risposta rapida</h2>
          <Field label="Data risposta" type="date" value={nc.supplier_response_date || ''} onChange={(v) => patch({ supplier_response_date: v })} />
          <TextField label="Messaggio a Evotech" rows={3} value={nc.supplier_response_text || ''} onChange={(v) => patch({ supplier_response_text: v })} placeholder="Riepilogo della vostra risposta..." />
          <TextField label="Causa radice (sintesi)" rows={2} value={nc.supplier_root_cause || ''} onChange={(v) => patch({ supplier_root_cause: v })} />
          <TextField label="Azione correttiva (sintesi)" rows={2} value={nc.supplier_corrective_action || ''} onChange={(v) => patch({ supplier_corrective_action: v })} />
        </div>

        {/* Report 8D */}
        <div style={{ background: 'white', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, color: '#334155' }}>Report 8D — Analisi strutturata</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
            Il metodo 8D guida un&apos;analisi rigorosa in 8 passi. Compila almeno D1-D5 per una chiusura efficace.
          </p>
          {[
            ['D1 — Team', 'd1_team', 2, 'Componenti del team (nome, ruolo, azienda)'],
            ['D2 — Descrizione problema', 'd2_problem_description', 3, '5W2H: What, When, Where, Who, Why, How, How many'],
            ['D3 — Azioni di contenimento', 'd3_containment_actions', 3, 'Cosa è stato fatto per bloccare il problema (segregazione, selezione 100%, blocco spedizioni). Includere data e responsabile.'],
            ['D4 — Analisi causa radice', 'd4_root_cause', 3, '5 Whys / Ishikawa — distinguere causa occorrenza vs causa mancata rilevazione'],
            ['D5 — Azioni correttive permanenti', 'd5_corrective_actions', 3, 'Azioni per eliminare la causa radice. Responsabile e data prevista.'],
            ['D6 — Verifica efficacia', 'd6_verification', 3, 'Come dimostrare che le azioni funzionano (dati, controlli, X lotti successivi)'],
            ['D7 — Prevenzione ricorrenza', 'd7_prevention', 3, 'Aggiornamento procedure, controlli in accettazione, formazione, FMEA / Piano di Controllo'],
            ['D8 — Chiusura', 'd8_closure_notes', 2, 'Riconoscimento team, lezioni apprese, condivisione'],
          ].map(([label, key, rows, placeholder]) => (
            <TextField key={key} label={label} rows={rows} value={nc[key] || ''} onChange={(v) => patch({ [key]: v })} placeholder={placeholder} />
          ))}
        </div>

        {/* Save bar */}
        <div style={{ position: 'sticky', bottom: 0, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 -4px 12px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            <b>Stato attuale:</b> {nc.status || '—'} · I dati verranno inviati direttamente a Evotech Precision.
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="portal-save-btn"
            style={{ background: '#0f172a', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 14, fontWeight: 'bold', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <Save style={{ width: 18, height: 18 }} />
            {saving ? 'Invio...' : 'Invia risposta a Evotech'}
          </button>
        </div>

        <div style={{ marginTop: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
          Se hai problemi con questo link, contatta Evotech Precision srl. <ExternalLink style={{ width: 12, height: 12, display: 'inline', verticalAlign: '-1px' }} />
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value, mono, highlight }) => (
  <div>
    <p style={{ margin: 0, fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold', letterSpacing: 0.5 }}>{label}</p>
    <p style={{ margin: '2px 0 0', fontFamily: mono ? 'monospace' : 'inherit', color: highlight ? '#dc2626' : '#0f172a', fontWeight: highlight ? 'bold' : 'normal' }}>
      {value ?? '—'}
    </p>
  </div>
);

const Field = ({ label, type = 'text', value, onChange }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: '#334155', marginBottom: 4 }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
    />
  </div>
);

const TextField = ({ label, rows = 3, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: '#334155', marginBottom: 4 }}>{label}</label>
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }}
    />
  </div>
);
